import { Injectable } from '@angular/core';

export enum FileSizeUnit {
  BYTES = 'bytes',
  KB = 'KB',
  MB = 'MB',
  GB = 'GB',
}

export interface FileValidationError {
  severity: 'error' | 'warn' | 'info';
  summary: string;
  detail: string;
}

export interface ValidationResult {
  isValid: boolean;
  error?: FileValidationError;
}

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = 1024 * 1024;
const BYTES_IN_GB = 1024 * 1024 * 1024;

const DESKTOP_FILENAME_LENGTH = 20;
const MOBILE_FILENAME_LENGTH = 10;
const DESKTOP_FILE_TYPE_LENGTH = 40;
const MOBILE_FILE_TYPE_LENGTH = 20;

@Injectable({
  providedIn: 'root',
})
export class FileValidationService {
  formatFileSize(sizeInBytes: number | null | undefined): string {
    if (!sizeInBytes || sizeInBytes <= 0) {
      return '';
    }

    if (sizeInBytes < BYTES_IN_KB) {
      return `${sizeInBytes} ${FileSizeUnit.BYTES}`;
    }

    if (sizeInBytes < BYTES_IN_MB) {
      return `${(sizeInBytes / BYTES_IN_KB).toFixed(1)} ${FileSizeUnit.KB}`;
    }

    if (sizeInBytes < BYTES_IN_GB) {
      return `${(sizeInBytes / BYTES_IN_MB).toFixed(1)} ${FileSizeUnit.MB}`;
    }

    return `${(sizeInBytes / BYTES_IN_GB).toFixed(1)} ${FileSizeUnit.GB}`;
  }

  truncateFilename(filename: string, maxLength: number): string {
    if (!filename || maxLength <= 0) {
      return '';
    }

    if (filename.length <= maxLength) {
      return filename;
    }

    const ellipsis = '...';
    const truncateLength = maxLength - ellipsis.length;

    if (truncateLength <= 0) {
      return ellipsis;
    }

    return filename.substring(0, truncateLength) + ellipsis;
  }

  validateFileSize(
    file: File,
    maxSize: number,
    isDesktop: boolean
  ): ValidationResult {
    if (file.size <= maxSize) {
      return { isValid: true };
    }

    const nameLength = isDesktop ? DESKTOP_FILENAME_LENGTH : MOBILE_FILENAME_LENGTH;
    const truncatedName = this.truncateFilename(file.name, nameLength);

    return {
      isValid: false,
      error: {
        severity: 'error',
        summary: `${truncatedName}: Invalid file size`,
        detail: `Maximum upload size is ${this.formatFileSize(maxSize)}`,
      },
    };
  }

  validateFileType(
    file: File,
    allowedTypes: string[],
    canSetMainFile: boolean,
    isDesktop: boolean
  ): ValidationResult {
    if (!allowedTypes || allowedTypes.length === 0) {
      return { isValid: true };
    }

    if (allowedTypes.includes(file.type)) {
      return { isValid: true };
    }

    const nameLength = isDesktop ? DESKTOP_FILE_TYPE_LENGTH : MOBILE_FILE_TYPE_LENGTH;
    const truncatedName = this.truncateFilename(file.name, nameLength);

    const supportedFormats = canSetMainFile
      ? 'Supports only JPG, PNG, and GIF'
      : 'Supports only PDF, MS Word, Excel, JPG, PNG, GIF and Zip';

    return {
      isValid: false,
      error: {
        severity: 'error',
        summary: `${truncatedName}: Invalid file type`,
        detail: supportedFormats,
      },
    };
  }

  validateFiles(
    files: File[],
    maxSize: number,
    allowedTypes: string[] | undefined,
    canSetMainFile: boolean,
    isDesktop: boolean
  ): ValidationResult {
    for (const file of files) {
      const sizeValidation = this.validateFileSize(file, maxSize, isDesktop);
      if (!sizeValidation.isValid) {
        return sizeValidation;
      }

      if (allowedTypes && allowedTypes.length > 0) {
        const typeValidation = this.validateFileType(
          file,
          allowedTypes,
          canSetMainFile,
          isDesktop
        );
        if (!typeValidation.isValid) {
          return typeValidation;
        }
      }
    }

    return { isValid: true };
  }

  convertFileListToArray(fileList: FileList): File[] {
    return Array.from(fileList);
  }
}
